/**
 * GFTY-4391 — double-dipping (biggest-of discount) cart DISPLAY helpers.
 *
 * All the DD pricing/display ALGORITHMS live here, in one file, so they can be read
 * and debugged in isolation instead of hunting through the ~1400-line cart-main.js.
 * The Alpine cart component (cart-main.js) keeps the reactive state + init/lifecycle
 * flow and DELEGATES to these functions. The storefront↔transform coupon bridge is a
 * separate snippet (snippets/dd-coupon-bridge.liquid). The server-side biggest-of math
 * is the cart-transform function (giftory-functions-app/biggest-discount).
 *
 * Every function here is PURE: it takes the cart line (and the AB arm as `variant`)
 * explicitly and returns a value — no Alpine `this`, no DOM, no side effects. `variant`
 * is the AB arm string; 'treatment' turns on the biggest-of display, anything else keeps
 * default Shopify behaviour. Loaded BEFORE cart-main.js (see sections/main-cart-items.liquid).
 */
(function () {
  'use strict';

  /**
   * The `search.variants` raw_pricing block for a line's variant, matched by SKU/id,
   * or null. Shared by the strike, code-tag, and List-Price logic.
   */
  function findRawPricing(item) {
    const list = item && item.variantsRawPricing;
    if (!Array.isArray(list)) return null;
    const sku = item.variant && item.variant.sku != null ? item.variant.sku.toString() : null;
    const vid = item.variant_id != null ? item.variant_id.toString()
              : (item.id != null ? item.id.toString() : null);
    const entry = list.find(e => {
      if (!e) return false;
      const eSku = e.sku != null ? e.sku.toString() : null;
      const eId = e.id != null ? e.id.toString() : null;
      return (sku && eSku && sku === eSku) || (vid && eId && vid === eId);
    });
    return entry && entry.raw_pricing ? entry.raw_pricing : null;
  }

  /**
   * Whether to show a line's code tag. In the treatment arm the transform neutralizes a
   * line so the code re-applies on top; when the BASE (everyday or flash) actually won the
   * biggest-of, the code is only a neutralize artifact and its "$ saved" is meaningless —
   * hide it. The code genuinely won only when the line was rebased to the FULL list (POG);
   * when the base won it was rebased BELOW POG (Edinburgh $29.59 vs POG $30, Red Lobster
   * $44.44 vs POG $50). So original_price ≈ POG → code won (show); below → hide. Control /
   * no-POG → always show (default Shopify behaviour).
   */
  /**
   * The list price (L) for a line, in CENTS — mirroring the cart transform's selection
   * (run.ts) EXACTLY so "code won" here matches the server's rebase decision. The transform
   * prefers the live compare_at as L; only when compare_at equals the DISPLAY price but NOT
   * the POG (i.e. compare_at is just the RRP) does it use the metafield POG; and it falls
   * back to the metafield POG when there's no compare_at. compare_at is in cents; the
   * metafield price_on_giftory / display_price are in dollars.
   */
  function listPriceCents(item) {
    const rp = findRawPricing(item);
    const ca = parseFloat(item.variant && item.variant.compare_at_price); // cents
    const pogD = rp ? parseFloat(rp.price_on_giftory) : NaN;              // dollars
    const dispD = rp ? parseFloat(rp.display_price) : NaN;               // dollars
    const caCents = !isNaN(ca) && ca > 0 ? ca : NaN;
    const pogCents = !isNaN(pogD) && pogD > 0 ? Math.round(pogD * 100) : NaN;
    const dispCents = !isNaN(dispD) && dispD > 0 ? Math.round(dispD * 100) : NaN;
    const near = (a, b) => Math.abs(a - b) < 1; // within a cent
    if (!isNaN(caCents) && !isNaN(pogCents) && !isNaN(dispCents) &&
        !near(caCents, pogCents) && near(caCents, dispCents)) {
      return pogCents; // compare_at is only the display → the true list is POG
    }
    if (!isNaN(caCents)) return caCents; // compare_at is the list
    return pogCents;                     // fallback: metafield POG (may be NaN)
  }

  function showCode(item, variant) {
    if (variant !== 'treatment' || !item) return true;
    const listCents = listPriceCents(item);
    const op = parseFloat(item.original_price); // per-unit, cents
    if (isNaN(listCents) || !(listCents > 0) || isNaN(op)) return true; // can't tell → show
    // The transform rebases a code-WON line to the FULL list (op == list) and a base-WON
    // line BELOW it (op == base/(1−rate) < list). So op ≈ list → code won (show); below → hide.
    return op >= listCents - 1;
  }

  /**
   * Strikethrough prices for a cart line, in CENTS × quantity, deduped and sorted
   * descending (highest first keeps its neutral colour). Treatment arm: raw_pricing
   * display_price then price_on_giftory (dollars, matched by SKU/id) — the raw_pricing
   * source of truth, never the line's original_price (which on a neutralized flash line
   * is an internal charge value, not a "was" price); for variants with no metafield (gift
   * cards) fall back to the variant compare_at. Control arm: variant compare_at_price +
   * original_price, or on a line-discounted line the single max(compare_at, display_price,
   * original_price). Only prices strictly above the per-unit selling price (discounted_price)
   * are kept. Mirrors the PDP strike logic.
   */
  function strikePrices(item, variant) {
    if (!item) return [];
    const qty = item.quantity || 1;
    const sellingCents = parseFloat(item.discounted_price);
    const cents = [];
    const rp = findRawPricing(item);

    if (variant === 'treatment') {
      if (rp) {
        const disp = parseFloat(rp.display_price);
        const pog = parseFloat(rp.price_on_giftory);
        if (!isNaN(disp)) cents.push(Math.round(disp * 100));
        if (!isNaN(pog)) cents.push(Math.round(pog * 100));
      } else {
        const ca = parseFloat(item.variant && item.variant.compare_at_price);
        if (!isNaN(ca)) cents.push(ca);
      }
    } else {
      const op = parseFloat(item.original_price);
      const lineDiscounted = !isNaN(sellingCents) && !isNaN(op) && Math.round(sellingCents) !== Math.round(op);
      const ca = parseFloat(item.variant && item.variant.compare_at_price);
      if (lineDiscounted) {
        // GFTY-5397: a line-level code (product/collection-scoped) moves discounted_price, so
        // original_price is only the Giftory price. Strike the single highest reference —
        // max(compare_at, raw_pricing display_price, original_price) — never lower than before.
        const disp = rp ? parseFloat(rp.display_price) : NaN;
        const refs = [ca, isNaN(disp) ? NaN : Math.round(disp * 100), op].filter(c => !isNaN(c) && c > 0);
        cents.push(Math.max(...refs));
      } else {
        if (!isNaN(ca)) cents.push(ca);
        if (!isNaN(op)) cents.push(op);
      }
    }

    const filtered = cents
      .map(c => Math.round(c))
      .filter(c => isNaN(sellingCents) ? true : c > sellingCents);
    const unique = [...new Set(filtered)].sort((a, b) => b - a);
    // TEMP: teal (--giftory-horizon) reverted to grey for now — force horizon:false so every strike
    // uses its base grey class. To restore the teal, change back to `idx !== 0`.
    return unique.map((c, idx) => ({ amount: c * qty, horizon: false }));
  }

  /**
   * The cart-summary "List Price" (beforeAnyDiscount) and "Product discounts" totals, in
   * cents.
   *   beforeAnyDiscount = Σ the HIGHEST reference price per line = max(variant.compare_at,
   *     raw_pricing.display_price) — so the total is the display ($140), not POG ($137,
   *     which is what variant.compare_at_price holds). Same in both arms.
   *   productDiscounts  = treatment: List Price − You Pay − coupon (the everyday markdown +
   *     flash; the code discounts show in their OWN "Coupon discounts" row). Control:
   *     product-discount (compare_at − original) + the FLASH SALE coupon total.
   *   couponDiscounts   = Σ NON-flash line allocations (the code discounts); shown as the
   *     "Coupon discounts" row and subtracted out of the treatment Product-discounts total.
   * Also returns flashSaleDiscountTotal so the caller can subtract it from cart.total_discount.
   * @returns {{ beforeAnyDiscount:number, productDiscounts:number, flashSaleDiscountTotal:number, couponDiscounts:number }}
   */
  function summary(items, itemsWithCompareAt, variant, youPay) {
    const list = Array.isArray(items) ? items : [];
    const withCompareAt = Array.isArray(itemsWithCompareAt) ? itemsWithCompareAt : [];

    const beforeAnyDiscount = list.reduce((acc, item) => {
      let top = item.variant.compare_at_price || item.original_price || 0; // cents
      const rp = findRawPricing(item);
      const disp = rp ? parseFloat(rp.display_price) : NaN;
      if (!isNaN(disp)) top = Math.max(top, Math.round(disp * 100));
      return acc + (top * item.quantity);
    }, 0);

    const productDiscounts = withCompareAt.reduce((acc, item) => {
      const compareAtPrice = item.variant.compare_at_price || item.original_price || 0;
      const originalPrice = item.original_price || 0;
      return acc + (compareAtPrice - originalPrice) * item.quantity;
    }, 0);

    // Treat FLASH SALE coupon discounts as "Product discounts" (own row), not "Coupon".
    const flashSaleDiscountTotal = list.reduce((acc, item) => {
      if (!Array.isArray(item.discounts) || item.discounts.length === 0) return acc;
      const itemFlashSaleTotal = item.discounts.reduce((sum, discount) => {
        if ((discount.title || '').toUpperCase() !== 'FLASH SALE') return sum;
        const rawAmount = discount.amount ?? 0;
        const parsedAmount = typeof rawAmount === 'string' ? parseInt(rawAmount, 10) : Number(rawAmount) || 0;
        return sum + parsedAmount;
      }, 0);
      return acc + itemFlashSaleTotal;
    }, 0);

    // Coupon (code) discounts = Σ NON-flash line allocations on lines where the code actually
    // WON. In treatment these get their own "Coupon discounts" row (cart-checkout-form.liquid),
    // so Product discounts EXCLUDES them: List − You Pay − coupon = the everyday markdown + flash.
    // GFTY-5041: gate on showCode (the SAME signal the per-line tag uses). On a base-won line the
    // transform rebases to POG so the code re-applies, leaving a GROSS neutralize-artifact
    // allocation whose money nets back to the everyday/flash price — counting it overstates the
    // Coupon row (e.g. −$109.14 instead of the real −$29.70) and understates Product discounts by
    // the same amount. showCode is true for control/no-POG, so control totals are unchanged.
    const couponDiscounts = list.reduce((acc, item) => {
      if (!Array.isArray(item.discounts) || item.discounts.length === 0) return acc;
      if (!showCode(item, variant)) return acc; // base (everyday/flash) won → its code allocation is an artifact
      const itemCouponTotal = item.discounts.reduce((sum, discount) => {
        if (((discount && discount.title) || '').toUpperCase() === 'FLASH SALE') return sum;
        const rawAmount = discount.amount ?? 0;
        const parsedAmount = typeof rawAmount === 'string' ? parseInt(rawAmount, 10) : Number(rawAmount) || 0;
        return sum + (Number.isFinite(parsedAmount) ? parsedAmount : 0);
      }, 0);
      return acc + itemCouponTotal;
    }, 0);

    const productDiscountsWithFlashSale = variant === 'treatment'
      ? Math.max(0, beforeAnyDiscount - youPay - couponDiscounts)
      : productDiscounts + flashSaleDiscountTotal;

    return {
      beforeAnyDiscount,
      productDiscounts: productDiscountsWithFlashSale,
      flashSaleDiscountTotal,
      couponDiscounts,
    };
  }

  /**
   * Diagnostic: the intermediate pricing values + decisions for a line, so a wrong tag/strike
   * can be read off directly instead of guessed. cart-main.js dumps this per line when
   * localStorage.dd_debug is set (`localStorage.dd_debug='1'` then reload).
   */
  function explain(item, variant) {
    const rp = findRawPricing(item);
    return {
      sku: item && item.variant && item.variant.sku,
      arm: variant,
      compareAt: item && item.variant && item.variant.compare_at_price,
      pog: rp ? rp.price_on_giftory : null,
      display: rp ? rp.display_price : null,
      original_price: item && item.original_price,
      final: item && item.discounted_price,
      listCents: listPriceCents(item),
      showCode: showCode(item, variant),
    };
  }

  /**
   * Whether a line's variant is DOUBLE-DIPPING-ELIGIBLE, independent of the AB arm / whether we
   * display it. Used to tag DD lines in the DOM (class `ddProduct`) so the experiment can be
   * scoped to users who saw a DD product. Rule: raw_pricing.discounted_price AND
   * raw_pricing.price_on_giftory are both present and different. display_price is irrelevant —
   * a POG-vs-display spread alone does NOT qualify (e.g. 149 / null / 129 → not eligible).
   * Keep in sync with the PDP server-side check (has_double_dip in bf_main-product.liquid).
   */
  function hasDoubleDip(item) {
    const rp = findRawPricing(item);
    if (!rp) return false;
    const priceOnGiftory = parseFloat(rp.price_on_giftory);
    const discountedPrice = parseFloat(rp.discounted_price);
    if (isNaN(priceOnGiftory) || isNaN(discountedPrice)) return false;
    return priceOnGiftory !== discountedPrice;
  }

  const api = { findRawPricing, listPriceCents, showCode, strikePrices, summary, explain, hasDoubleDip };
  // Browser: expose on window for cart-main.js. Node (vitest): expose via CommonJS so the
  // pure functions can be unit-tested (see tests/dd-cart.test.js). `window` is guarded so
  // requiring this file under Node doesn't throw.
  if (typeof window !== 'undefined') window.DDCart = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
